import React, { useState, useRef, useEffect } from 'react';
import '../Properties/property.scss';
import PropTypes from 'prop-types';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import CloseIcon from '@mui/icons-material/Close';
import PropertyLayout from './layout';
import { uploadFiles, addPropertyDetail, fetchFileById } from '../../Api/request/propertyDetail';
import UseAxios from '../../Api/http/http';
import useApiState from '../../Hooks/useApiState';
import { propertyAPI } from '../../services/api';

const MAX_IMAGE_COUNT = 20;
const MAX_IMAGE_SIZE_MB = 10;
const MAX_VIDEO_COUNT = 3;
const MAX_VIDEO_SIZE_MB = 500;

const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/jpg'];
const ACCEPTED_VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/x-matroska', 'video/webm'];

function PropertyImages({ prevStep, onNext, onSave, onBackToDashboard, initialValues = {}, step = 4, totalSteps = 10, systemId, isEdit = false, isView = false }) {
  const initialState = {
    images: [],
    videos: [],
  };
  const [form, setForm] = useState(() => {
    // Don't include images/videos from initialValues yet - they'll be loaded properly in useEffect
    // eslint-disable-next-line no-unused-vars
    const { images, videos, property_images, property_videos, ...restInitialValues } = initialValues;
    return { ...initialState, ...restInitialValues };
  });
  const [error, setError] = useState('');
  const [previewImg, setPreviewImg] = useState(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const fileInputRef = useRef();

  // Use the API state hook
  const {
    isSubmitting,
    submitError,
    clearErrors,
    submitData,
  } = useApiState({
    showToastOnError: true,
    defaultErrorMessage: 'Failed to save property images',
  });

  // Function to fetch existing files for edit mode
  const fetchExistingFiles = async (fileList, fileType) => {
    if (!fileList || !Array.isArray(fileList) || fileList.length === 0) return [];

    console.log(`Fetching ${fileList.length} ${fileType} files...`);
    const fetchedFiles = [];
    for (const fileInfo of fileList) {
      try {
        const response = await UseAxios(fetchFileById({ fileId: fileInfo.id }));
        if (response && response.data) {
          let blob;
          if (response.data instanceof Blob) {
            blob = response.data;
          } else if (response.data instanceof ArrayBuffer) {
            blob = new Blob([response.data], { type: fileInfo.type || 'application/octet-stream' });
          } else {
            console.error(`Invalid response data type for ${fileType} file:`, fileInfo.name);
            continue; // Skip this file if data is invalid
          }
          if (blob && blob.size > 0) { // Ensure blob has content
            const url = URL.createObjectURL(blob);
            const file = new File([blob], fileInfo.name, { type: fileInfo.type });
            fetchedFiles.push({
              url,
              name: fileInfo.name,
              file,
              type: fileInfo.type,
              id: fileInfo.id, // Keep the original file ID for reference
              uploadUrl: fileInfo.uploadUrl, // Preserve upload URL
              gcsUrl: fileInfo.gcsUrl, // Preserve GCS URL
            });
          } else {
            console.error(`Empty or invalid blob for ${fileType} file:`, fileInfo.name);
          }
        } else {
          console.error(`No data in response for ${fileType} file:`, fileInfo.name);
        }
      } catch (error) {
        console.error(`Error fetching ${fileType} file:`, error);
      }
    }

    console.log(`Successfully fetched ${fetchedFiles.length} ${fileType} files`);
    return fetchedFiles;
  };

  // Load existing files when in edit mode
  useEffect(() => {
    const loadExistingFiles = async () => {
      if ((isEdit || isView) && initialValues) {
        setIsLoadingFiles(true);
        try {
          let existingImages = [];
          let existingVideos = [];

          // Fetch existing images using fetchFileById
          if (initialValues.property_images && Array.isArray(initialValues.property_images)) {
            existingImages = await fetchExistingFiles(initialValues.property_images, 'image');
          }

          // Fetch existing videos using fetchFileById
          if (initialValues.property_videos && Array.isArray(initialValues.property_videos)) {
            existingVideos = await fetchExistingFiles(initialValues.property_videos, 'video');
          }

          setForm(prev => {
            // Check if we already have user-uploaded images that shouldn't be overridden
            const hasUserImages = prev.images && prev.images.length > 0;
            const hasUserVideos = prev.videos && prev.videos.length > 0;

            if (hasUserImages || hasUserVideos) {
              // Merge existing files with user uploads
              const mergedImages = hasUserImages ?
                [...existingImages, ...prev.images] :
                existingImages;
              const mergedVideos = hasUserVideos ?
                [...existingVideos, ...prev.videos] :
                existingVideos;

              return {
                ...prev,
                images: mergedImages,
                videos: mergedVideos,
              };
            }

            // Set the properly fetched images and videos
            return {
              ...prev,
              images: existingImages,
              videos: existingVideos,
            };
          });
        } catch (error) {
          console.error('Error loading existing files:', error);
          setError('Failed to load existing files');
        } finally {
          setIsLoadingFiles(false);
        }
      } else if (initialValues) {
        setForm(prev => {
          // For non-edit mode, just merge initialValues without fetching files
          return { ...prev, ...initialValues };
        });
      }
    };

    loadExistingFiles();
  }, [initialValues, isEdit, isView]);

  // Cleanup blob URLs when component unmounts
  useEffect(() => {
    return () => {
      // Only revoke blob URLs when actually unmounting, not when navigating
      // The parent component should handle storing files properly
      form.images.forEach(img => {
        if (img.url && img.url.startsWith('blob:') && !img.id) {
          // Only revoke new uploads that aren't persisted yet
          URL.revokeObjectURL(img.url);
        }
      });
      form.videos.forEach(video => {
        if (video.url && video.url.startsWith('blob:') && !video.id) {
          // Only revoke new uploads that aren't persisted yet
          URL.revokeObjectURL(video.url);
        }
      });
    };
  }, []); // Empty dependency array - only run on unmount

  // Sync initialValues if they change (for navigation between steps)
  useEffect(() => {
    if (initialValues && !isEdit && !isView) {
      setForm((prev) => {
        // Check if initialValues has media data that should be preserved
        const hasInitialImages = initialValues.images &&
          Array.isArray(initialValues.images) &&
          initialValues.images.length > 0;
        const hasInitialVideos = initialValues.videos &&
          Array.isArray(initialValues.videos) &&
          initialValues.videos.length > 0;

        // Recreate blob URLs for files that need them
        let restoredImages = [];
        if (hasInitialImages) {
          restoredImages = initialValues.images.map(img => {
            // If the blob URL was revoked or doesn't exist, recreate it
            if (img.file && (!img.url || img.url.startsWith('blob:') && !img.url.includes('blob:'))) {
              const newUrl = URL.createObjectURL(img.file);
              return { ...img, url: newUrl };
            }
            return img;
          });
        }

        let restoredVideos = [];
        if (hasInitialVideos) {
          restoredVideos = initialValues.videos.map(video => {
            // If the blob URL was revoked or doesn't exist, recreate it
            if (video.file && (!video.url || video.url.startsWith('blob:') && !video.url.includes('blob:'))) {
              const newUrl = URL.createObjectURL(video.file);
              return { ...video, url: newUrl };
            }
            return video;
          });
        }

        // If we have initial media, use the restored versions
        if (hasInitialImages || hasInitialVideos) {
          return {
            ...prev,
            ...initialValues,
            images: restoredImages.length > 0 ? restoredImages : prev.images,
            videos: restoredVideos.length > 0 ? restoredVideos : prev.videos,
          };
        }

        return prev;
      });
    }
  }, [initialValues, isEdit, isView]);

  const handleFiles = (files) => {
    setError('');
    clearErrors(); // Clear API errors when user interacts with files
    const fileArray = Array.from(files);
    let newImages = [];
    let newVideos = [];
    let imageCount = form.images.length;
    let videoCount = form.videos.length;
    for (let file of fileArray) {
      if (ACCEPTED_IMAGE_TYPES.includes(file.type)) {
        if (imageCount >= MAX_IMAGE_COUNT) {
          setError(`You can upload up to ${MAX_IMAGE_COUNT} images.`);
          continue;
        }
        if (file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) {
          setError(`Image ${file.name} exceeds ${MAX_IMAGE_SIZE_MB}MB.`);
          continue;
        }
        newImages.push({ url: URL.createObjectURL(file), name: file.name, file: file, type: file.type });
        imageCount++;
      } else if (ACCEPTED_VIDEO_TYPES.includes(file.type)) {
        if (videoCount >= MAX_VIDEO_COUNT) {
          setError(`You can upload up to ${MAX_VIDEO_COUNT} videos.`);
          continue;
        }
        if (file.size > MAX_VIDEO_SIZE_MB * 1024 * 1024) {
          setError(`Video ${file.name} exceeds ${MAX_VIDEO_SIZE_MB}MB.`);
          continue;
        }
        newVideos.push({ url: URL.createObjectURL(file), name: file.name, file: file, type: file.type });
        videoCount++;
      } else {
        setError(`File type not supported: ${file.name}`);
      }
    }
    setForm((prev) => ({
      ...prev,
      images: [...prev.images, ...newImages].slice(0, MAX_IMAGE_COUNT),
      videos: [...prev.videos, ...newVideos].slice(0, MAX_VIDEO_COUNT),
    }));
    if (fileInputRef.current) fileInputRef.current.value = null; // Reset input
  };

  const handleFileSelect = (event) => {
    handleFiles(event.target.files);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragActive(false);
    handleFiles(event.dataTransfer.files);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const callDeleteMediaApi = (mediaItem) => {
    const fileIdToDelete =
      mediaItem?.id ||
      mediaItem?.fileId ||
      mediaItem?.file_id ||
      mediaItem?.driveFileId ||
      null;

    if (!fileIdToDelete) return;

    void propertyAPI.deletePropertyImage(String(fileIdToDelete)).catch((error) => {
      console.warn('Failed to delete media from drive', {
        fileId: String(fileIdToDelete),
        error,
      });
    });
  };

  const removeImage = (index) => {
    setForm((prev) => {
      const img = prev.images[index];
      callDeleteMediaApi(img);
      // Revoke blob URLs for all images
      if (img && img.url && img.url.startsWith('blob:')) {
        URL.revokeObjectURL(img.url);
      }
      if (fileInputRef.current) fileInputRef.current.value = null; // Reset input
      return { ...prev, images: prev.images.filter((img, idx) => idx !== index) };
    });
  };
  const removeVideo = (index) => {
    setForm((prev) => {
      const video = prev.videos[index];
      callDeleteMediaApi(video);
      // Revoke blob URLs for all videos
      if (video && video.url && video.url.startsWith('blob:')) {
        URL.revokeObjectURL(video.url);
      }
      return { ...prev, videos: prev.videos.filter((vid, idx) => idx !== index) };
    });
  };

  const handleRemovePreviewImg = () => {
    setForm((prev) => {
      const img = prev.images.find((img) => img.url === previewImg);
      callDeleteMediaApi(img);
      // Revoke blob URLs for all images
      if (img && img.url && img.url.startsWith('blob:')) {
        URL.revokeObjectURL(img.url);
      }
      if (fileInputRef.current) fileInputRef.current.value = null; // Reset input
      return { ...prev, images: prev.images.filter((img) => img.url !== previewImg) };
    });
    setPreviewImg(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    clearErrors();

    try {
      await submitData(async () => {
        // Separate existing files (with IDs) from new files (without IDs) for images
        const existingImages = form.images.filter(img => img.id);
        const newImages = form.images.filter(img => !img.id);

        // Upload only new images
        let allImageMetaData = [];
        if (newImages.length > 0) {
          const formData = new FormData();
          formData.append('systemId', systemId);
          for (const media of newImages) {
            if (media.file instanceof File) {
              formData.append('images', media.file, media.name);
            }
          }
          formData.append('type', 'Photos');
          const response = await UseAxios(uploadFiles(formData));
          if (response?.data?.receiptData) {
            allImageMetaData = [...allImageMetaData, ...response.data.receiptData];
          }
        }

        // Add existing images metadata (preserve original IDs and structure)
        const existingImageMetaData = existingImages.map(img => ({
          id: img.id,
          name: img.name,
          type: img.type,
          uploadUrl: img.uploadUrl,
          gcsUrl: img.gcsUrl,
        }));
        allImageMetaData = [...existingImageMetaData, ...allImageMetaData];

        // Separate existing files (with IDs) from new files (without IDs) for videos
        const existingVideos = form.videos.filter(video => video.id);
        const newVideos = form.videos.filter(video => !video.id);

        // Upload only new videos
        let allVideoMetaData = [];
        if (newVideos.length > 0) {
          const formData = new FormData();
          formData.append('systemId', systemId);
          for (const media of newVideos) {
            if (media.file instanceof File) {
              formData.append('images', media.file, media.name);
            }
          }
          formData.append('type', 'Videos');
          const response = await UseAxios(uploadFiles(formData));
          if (response?.data?.receiptData) {
            allVideoMetaData = [...allVideoMetaData, ...response.data.receiptData];
          }
        }

        // Add existing videos metadata (preserve original IDs and structure)
        const existingVideoMetaData = existingVideos.map(video => ({
          id: video.id,
          name: video.name,
          type: video.type,
          uploadUrl: video.uploadUrl,
          gcsUrl: video.gcsUrl,
        }));
        allVideoMetaData = [...existingVideoMetaData, ...allVideoMetaData];

        // Always save images meta data (existing + new) - even if empty to handle deletions
        const combinedImageMetaData = {
          imageSave: true,
          property_id: systemId,
          imageMetaData: allImageMetaData,
        };
        // Always save videos meta data (existing + new) - even if empty to handle deletions
        const combinedVideoMetaData = {
          videoSave: true,
          property_id: systemId,
          videoMetaData: allVideoMetaData,
        };
        // Always call save API for images (even if array is empty to handle deletions)
        await UseAxios(addPropertyDetail(combinedImageMetaData));
        // Always call save API for videos (even if array is empty to handle deletions)
        await UseAxios(addPropertyDetail(combinedVideoMetaData));

        return form;
      }, {
        successMessage: 'Property images saved successfully',
      });

      // The form data is already updated in the submitData callback
      onNext(form);
    } catch (error) {
      console.error('Failed to save property images:', error);
    }
  };
  const handlePrev = () => {
    onSave(form); // Save current form data including uploaded files
    prevStep(); // Go to previous step
  };

  const handleNext = () => {
    onSave(form); // Save current form data
    onNext(form); // Pass form data to next step
  };

  return (
    <PropertyLayout step={step} totalSteps={totalSteps} systemId={systemId} onBackToDashboard={onBackToDashboard}>
      <div className="card">
        <h3 className="card-title">
          <PhotoCameraIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
          Property Images
          {isEdit && (
            <span style={{ fontSize: '0.8em', color: '#666', marginLeft: '10px' }}>
              (Edit Mode)
            </span>
          )}
          {isView && (
            <span style={{ fontSize: '0.8em', color: '#666', marginLeft: '10px' }}>
              (View Mode)
            </span>
          )}
        </h3>

        {/* Display loading for existing files */}
        {isLoadingFiles && (
          <div className="loading-message" style={{
            padding: '10px',
            marginBottom: '15px',
            backgroundColor: '#e3f2fd',
            color: '#1976d2',
            borderRadius: '4px',
            border: '1px solid #bbdefb',
            textAlign: 'center',
          }}>
            Loading existing images and videos...
          </div>
        )}

        {/* Display API errors */}
        {submitError && (
          <div className="error-message" style={{
            padding: '10px',
            marginBottom: '15px',
            backgroundColor: '#ffebee',
            color: '#c62828',
            borderRadius: '4px',
            border: '1px solid #ffcdd2',
          }}>
            {submitError}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {!isView && (
            <div
              className={`upload-box${isDragActive ? ' drag-active' : ''}`}
              onDrop={isSubmitting ? undefined : handleDrop}
              onDragOver={isSubmitting ? undefined : handleDragOver}
              onDragEnter={isSubmitting ? undefined : () => setIsDragActive(true)}
              onDragLeave={isSubmitting ? undefined : () => setIsDragActive(false)}
              style={{ cursor: isSubmitting ? 'not-allowed' : 'default', opacity: isSubmitting ? 0.6 : 1 }}
            >
              <PhotoCameraIcon className="upload-icon" />
              <p>Upload property photos and videos</p>
              <span className="hint-text">Click or drag images/videos here</span>
              <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                <button
                  type="button"
                  className="btn small"
                  style={{ cursor: 'pointer' }}
                  onClick={event => {
                    event.stopPropagation();
                    fileInputRef.current.click();
                  }}
                  disabled={isSubmitting}
                >
                  Choose Files
                </button>
              </div>
              <input
                type="file"
                multiple
                accept="image/*,video/*"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileSelect}
                disabled={isSubmitting}
              />
              {error && <div className="field-note-error" style={{ color: 'red', marginTop: 8 }}>{error}</div>}
            </div>
          )}

          {(form.images.length > 0 || form.videos.length > 0) && (
            <div className="preview-container" style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
              {form.images.filter(img => img.url).map((img, idx) => (
                <div
                  key={idx}
                  className="preview-wrapper"
                  style={{ position: 'relative', width: 130, marginBottom: 16 }}
                >
                  {!isView && (
                    <span
                      className="cancel-icon"
                      style={{ position: 'absolute', top: 4, right: 4, zIndex: 2, cursor: 'pointer' }}
                      onClick={(event) => {
                        event.stopPropagation();
                        removeImage(idx);
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </span>
                  )}
                  {img.url ? (
                    <img
                      src={img.url}
                      alt={img.name}
                      className="preview-image"
                      style={{ display: 'block', width: 120, height: 90, objectFit: 'cover', borderRadius: 8, border: '1px solid #eee', margin: '0 auto', cursor: 'pointer' }}
                      onClick={() => setPreviewImg(img.url)}
                      onError={(event) => {
                        console.error('Failed to load image:', img.name);
                        event.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 120,
                        height: 90,
                        backgroundColor: '#f5f5f5',
                        borderRadius: 8,
                        border: '1px solid #eee',
                        margin: '0 auto',
                        fontSize: 12,
                        color: '#999',
                      }}
                    >
                      Loading...
                    </div>
                  )}
                  <div className="preview-label" style={{ textAlign: 'center', fontSize: 12, marginTop: 4, wordBreak: 'break-all' }}>{img.name}</div>
                </div>
              ))}
              {form.videos.filter(vid => vid.url).map((vid, idx) => (
                <div
                  key={idx}
                  className="preview-wrapper"
                  style={{ position: 'relative', width: 130, marginBottom: 16 }}
                >
                  {!isView && (
                    <span
                      className="cancel-icon"
                      style={{ position: 'absolute', top: 4, right: 4, zIndex: 2, cursor: 'pointer' }}
                      onClick={(event) => {
                        event.stopPropagation();
                        removeVideo(idx);
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </span>
                  )}
                  <video
                    src={vid.url}
                    className="preview-image"
                    style={{ display: 'block', width: 120, height: 90, objectFit: 'cover', borderRadius: 8, border: '1px solid #eee', margin: '0 auto' }}
                    controls
                  />
                  <div className="preview-label" style={{ textAlign: 'center', fontSize: 12, marginTop: 4, wordBreak: 'break-all' }}>{vid.name}</div>
                </div>
              ))}
            </div>
          )}

          {previewImg && (
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                background: 'rgba(0,0,0,0.7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 9999,
              }}
              onClick={() => setPreviewImg(null)}
            >
              <div style={{ position: 'relative' }} onClick={event => event.stopPropagation()}>
                {!isView && (
                  <span
                    className="cancel-icon"
                    style={{ position: 'absolute', top: 8, right: 8, zIndex: 2, cursor: 'pointer', background: '#fff', borderRadius: '50%', padding: 2 }}
                    onClick={handleRemovePreviewImg}
                  >
                    <CloseIcon fontSize="large" />
                  </span>
                )}
                <img
                  src={previewImg}
                  alt="Preview"
                  style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 12, boxShadow: '0 2px 16px #0008' }}
                />
              </div>
            </div>
          )}

          <div className="tips-section" style={{ textAlign: 'left' }}>
            <h4>Tips for great property photos:</h4>
            <ul>
              <li>Use natural lighting</li>
              <li>Include main rooms and exterior</li>
              <li>Highlight unique features</li>
            </ul>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn secondary"
              onClick={handlePrev}
              disabled={isSubmitting}
            >
              Previous
            </button>
            {isView ? (
              <button
                type="button"
                className="btn primary"
                onClick={handleNext}
              >
                Next
              </button>
            ) : (
              <button
                type="submit"
                className="btn primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Uploading...' : 'Next'}
              </button>
            )}
          </div>
        </form>
      </div>
    </PropertyLayout>
  );
}

PropertyImages.propTypes = {
  prevStep: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onBackToDashboard: PropTypes.func,
  initialValues: PropTypes.object,
  step: PropTypes.number,
  totalSteps: PropTypes.number,
  systemId: PropTypes.string,
  isEdit: PropTypes.bool,
  isView: PropTypes.bool,
};

export default PropertyImages;
